import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { listingsAPI, ordersAPI, reviewsAPI, userAPI, watchlistAPI, usersAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import {
    formatCurrency,
    formatDate,
    getInitials,
} from '@/lib/utils';
import {
    Package,
    Star,
    Gavel,
    Settings,
    TrendingUp,
    ShoppingBag,
    Heart,
    Users,
    Mail,
    AlertTriangle,
    Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

// Import refactored components
import {
    DashboardSettingsTab,
    DashboardListingsTab,
    DashboardBidsTab,
    DashboardOrdersTab,
    DashboardReviewsTab,
    DashboardAuctionsTab
} from '@/components/dashboard';

// Import new components
import DashboardLikedItemsTab from '@/components/dashboard/DashboardLikedItemsTab';
import DashboardParticipatingTab from '@/components/dashboard/DashboardParticipatingTab';
import DashboardMyListingsTab from '@/components/dashboard/DashboardMyListingsTab';
import DashboardFollowingTab from '@/components/dashboard/DashboardFollowingTab';

const DashboardPage = () => {
    const { user, isAuthenticated, isLoggingOut, updateUser } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'for-sale');
    const [loading, setLoading] = useState(true);
    const [myListings, setMyListings] = useState([]);
    const [myBidAuctions, setMyBidAuctions] = useState([]);
    const [myOrders, setMyOrders] = useState({ buying: [], selling: [] });
    const [reviews, setReviews] = useState([]);
    const [resendingVerification, setResendingVerification] = useState(false);
    const [tabCounts, setTabCounts] = useState({ participating: 0, buying: 0, sold: 0, forSale: 0, favorites: 0, following: 0 });
    
    // Autohide state for tab bar
    const [isTabBarVisible, setIsTabBarVisible] = useState(true);
    const [lastScrollY, setLastScrollY] = useState(0);

    // Autohide effect
    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            
            if (currentScrollY < 100) {
                setIsTabBarVisible(true);
            } else if (currentScrollY < lastScrollY) {
                setIsTabBarVisible(true);
            } else if (currentScrollY > lastScrollY) {
                setIsTabBarVisible(false);
            }
            
            setLastScrollY(currentScrollY);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [lastScrollY]);

    // Sync activeTab with URL query params
    useEffect(() => {
        const tabFromUrl = searchParams.get('tab');
        if (tabFromUrl && tabFromUrl !== activeTab) {
            setActiveTab(tabFromUrl);
        }
    }, [searchParams]);

    useEffect(() => {
        // Only redirect to login if user is not authenticated and not currently logging out
        if (!isAuthenticated && !isLoggingOut) {
            navigate('/login');
            return;
        }

        const fetchData = async () => {
            try {
                const [listingsRes, myBidsRes, buyOrdersRes, sellOrdersRes, favoritesRes, followingRes] = await Promise.all([
                    listingsAPI.getMy(),
                    listingsAPI.getMyBids(),
                    ordersAPI.getAll('buyer'),
                    ordersAPI.getAll('seller'),
                    watchlistAPI.getMy().catch(() => ({ data: [] })),
                    usersAPI.getFollowedSellers().catch(() => ({ data: [] })),
                ]);

                const listings = Array.isArray(listingsRes.data) ? listingsRes.data : [];
                const bids = Array.isArray(myBidsRes.data) ? myBidsRes.data : [];
                const buyOrders = Array.isArray(buyOrdersRes.data) ? buyOrdersRes.data : [];
                const favorites = Array.isArray(favoritesRes.data) ? favoritesRes.data : [];
                const following = Array.isArray(followingRes.data) ? followingRes.data : [];

                setMyListings(listings);
                setMyBidAuctions(bids);
                
                // For "Vândute" tab, include ALL seller orders (not just completed) plus active listings
                const sellingOrders = Array.isArray(sellOrdersRes.data) ? sellOrdersRes.data : [];
                
                setMyOrders({
                    buying: buyOrders,
                    selling: sellingOrders,
                });

                // Calculate sold count: only non-finalized sales (exclude completed, cancelled)
                const finalizedStatuses = ['completed', 'cancelled'];
                const nonFinalizedSoldCount = listings.filter(l => {
                    if (l.status !== 'sold') return false;
                    // Find the corresponding order
                    const order = sellingOrders.find(o => o.listing_id === l.listing_id);
                    // If no order or order is not finalized, count it
                    if (!order) return true;
                    return !finalizedStatuses.includes(order.order_status);
                }).length;

                // Update tab counts
                setTabCounts({
                    participating: bids.length,
                    buying: buyOrders.length,
                    forSale: listings.filter(l => l.status === 'active').length,
                    sold: nonFinalizedSoldCount,
                    favorites: favorites.length,
                    following: following.length
                });
                
                // Fetch reviews separately to handle errors
                if (user?.username) {
                    try {
                        const reviewsRes = await reviewsAPI.getByUsername(user.username);
                        setReviews(reviewsRes.data || []);
                    } catch (e) {
                        console.error('Error fetching reviews:', e);
                        setReviews([]);
                    }
                }
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [isAuthenticated, navigate, user?.username, isLoggingOut]);

    const handleResendVerification = async () => {
        if (!user?.email) return;
        
        setResendingVerification(true);
        try {
            await api.post('/auth/resend-verification', { email: user.email });
            toast.success('Email de verificare trimis! Verifică-ți inbox-ul.');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la trimiterea email-ului');
        } finally {
            setResendingVerification(false);
        }
    };

    if (loading) {
        return (
            <div className="pt-0">
                <div className="sticky top-[64px] z-40 pointer-events-none">
                    <div className="w-full flex justify-center">
                        <Skeleton className="h-12 w-[700px] rounded-b-2xl" />
                    </div>
                </div>
                <div className="container mx-auto py-6 px-4">
                    <Skeleton className="h-[400px] rounded-2xl" />
                </div>
            </div>
        );
    }

    return (
        <div className="pt-0">
            {/* Email Verification Banner */}
            {user && !user.email_verified && (
                <div className="bg-amber-50 border-b border-amber-200">
                    <div className="container mx-auto px-4 py-3">
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                                    <Mail className="w-5 h-5 text-amber-600" />
                                </div>
                                <div>
                                    <p className="font-medium text-amber-900">Verifică-ți adresa de email</p>
                                    <p className="text-sm text-amber-700">
                                        Pentru a avea acces la toate funcțiile, te rugăm să îți verifici adresa de email.
                                    </p>
                                </div>
                            </div>
                            <Button
                                onClick={handleResendVerification}
                                disabled={resendingVerification}
                                variant="outline"
                                className="border-amber-300 text-amber-700 hover:bg-amber-100 whitespace-nowrap"
                            >
                                {resendingVerification ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Se trimite...
                                    </>
                                ) : (
                                    <>
                                        <Mail className="w-4 h-4 mr-2" />
                                        Retrimite email
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                {/* Tab Content */}
                <div className="container mx-auto py-6 px-4">
                    {/* Participating Tab - Auctions user is participating in (bidding) */}
                    <TabsContent value="participating" className="mt-0">
                        <DashboardParticipatingTab />
                    </TabsContent>

                    {/* Favorites Tab */}
                    <TabsContent value="favorites" className="mt-0">
                        <DashboardLikedItemsTab />
                    </TabsContent>

                    {/* Following Tab - Users the current user is following */}
                    <TabsContent value="following" className="mt-0">
                        <DashboardFollowingTab />
                    </TabsContent>

                    {/* Buying Tab - User's buying history */}
                    <TabsContent value="buying" className="mt-0">
                        <DashboardOrdersTab orders={myOrders.buying} type="buying" />
                    </TabsContent>

                    {/* Selling Tab - User's listings (active + sold) and orders */}
                    <TabsContent value="selling" className="mt-0">
                        <DashboardMyListingsTab />
                    </TabsContent>

                    {/* For Sale Tab - User's active listings only */}
                    <TabsContent value="for-sale" className="mt-0">
                        <DashboardMyListingsTab filterStatus="active" />
                    </TabsContent>

                    {/* Sold Tab - User's sold listings only */}
                    <TabsContent value="sold" className="mt-0">
                        <DashboardMyListingsTab filterStatus="sold" />
                    </TabsContent>

                    {/* Legacy tabs for compatibility */}
                    <TabsContent value="my-listings" className="mt-0">
                        <DashboardMyListingsTab />
                    </TabsContent>

                    {/* My Bids Tab - User's auction bid history (accessible via menu) */}
                    <TabsContent value="reviews" className="mt-0">
                        <DashboardReviewsTab reviews={reviews} />
                    </TabsContent>

                    {/* Settings Tab (accessible via menu) */}
                    <TabsContent value="settings" className="mt-0">
                        <DashboardSettingsTab user={user} updateUser={updateUser} />
                    </TabsContent>

                    {/* Auctions Tab (accessible via menu) */}
                    <TabsContent value="auctions" className="mt-0">
                        <DashboardAuctionsTab />
                    </TabsContent>

                    {/* Legacy tabs for compatibility */}
                    <TabsContent value="listings" className="mt-0">
                        <DashboardListingsTab listings={myListings} />
                    </TabsContent>
                </div>
            </Tabs>
        </div>
    );
};

export default DashboardPage;
